export const training = {
  title: 'Behavioral Training',
  subtitle: 'Learn how to read and interpret behavioral profiles',
  
  modules: {
    foundations: {
      title: 'Foundations',
      description: 'Learn the basics of reading profiles'
    },
    framework: {
      title: 'The Framework',
      description: 'Deep dive into the five-dimension behavioral model'
    },
    application: {
      title: 'Application',
      description: 'Put your knowledge to work in real scenarios'
    }
  },
  
  responsibleUse: {
    title: 'Responsible Use',
    points: [
      'Profiles are one data point—combine with interviews and experience',
      'Scores show preferences, not capabilities or limitations',
      'Context matters—the same profile can excel or struggle depending on the role',
      'Never use profiles to make pass/fail hiring decisions',
      'Profiles should not be used to screen out candidates from protected groups',
      'Individual results may vary—always interview candidates regardless of profile match'
    ]
  },
  
  legalDisclaimer: {
    title: 'Legal Disclaimer & Non-Discrimination Notice',
    points: [
      'This assessment is not a psychological evaluation, medical screening, or disability diagnostic tool',
      'Behavioral profiles must never be the sole basis for employment decisions',
      'Do not use profiles to exclude candidates based on characteristics that may correlate with protected classes (race, color, religion, sex, national origin, age, disability, genetic information)',
      'Organizations must comply with applicable employment laws including Title VII, ADA, ADEA, and GINA',
      'Reasonable accommodations may affect how individuals respond to assessments—consult with legal counsel',
      'Role matching examples are illustrative only—actual job-fit determinations require formal job analysis validated for your specific roles',
      'Employers are solely responsible for ensuring their use of this tool complies with all applicable federal, state, and local employment laws'
    ]
  },
  
  sections: {
    chart: {
      title: 'Understanding the Chart',
      subtitle: 'How to read behavioral visualizations',
    },
    percentile: {
      title: 'The Percentile Scale',
      subtitle: 'What scores mean in context',
    },
    traits: {
      title: 'The Five Behavioral Traits',
      subtitle: 'Understanding D-C-T-O-S dimensions',
    },
    width: {
      title: 'Profile Width: Wide vs Narrow',
      subtitle: 'What the spread of scores tells you',
    },
    cognitive: {
      title: 'Cognitive Assessment',
      subtitle: 'Mental ability and problem-solving',
    },
    reasoning: {
      title: 'Reasoning Styles',
      subtitle: 'How people approach problem-solving',
    },
    executionSpeed: {
      title: 'Execution Speed: The D-T Gap',
      subtitle: 'How the Drive-Task relationship affects pace',
    },
    applications: {
      title: 'Practical Applications',
      subtitle: 'How to use profiles in the real world',
    },
    talentDesign: {
      title: 'Talent Design: Building the Right Team',
      subtitle: 'The Gas-Glue-Brake framework for team composition',
    }
  },

  chartExplainer: {
    title: 'Understanding the Chart',
    subtitle: 'Every behavioral profile displays as a visual chart. Here\'s how to read it:',
    traits: {
      dominance: 'Drive',
      extraversion: 'Connection',
      patience: 'Task',
      formality: 'Order',
      objectivity: 'Style'
    },
    colorLegend: {
      title: 'Color-Coded Traits'
    },
    position: {
      title: 'Dot Position Meaning',
      left: {
        label: 'Left Side (0-40%)',
        description: 'Lower expression of the trait'
      },
      center: {
        label: 'Center (40-60%)',
        description: 'Moderate, balanced expression'
      },
      right: {
        label: 'Right Side (60-100%)',
        description: 'Higher expression of the trait'
      }
    },
    howToRead: {
      title: 'How to read:',
      description: 'Hover over any dot to see the exact percentile score. The visual spread shows at a glance whether someone has a wide (specialized) or narrow (versatile) profile.'
    }
  },

  percentileScale: {
    title: 'The Percentile Scale',
    subtitle: 'All scores are shown as percentiles (0-100%) comparing someone to the general population.',
    fiftyPercent: {
      title: 'What Does 50% Mean?',
      description: '50% is the population average. Most people score between 40-60% on each trait. Scores follow a bell curve distribution.'
    },
    understanding: {
      title: 'Understanding Score Ranges'
    },
    ranges: {
      low: {
        label: 'Low Score',
        description: 'Trait is less prominent - reflects the opposite behavioral tendency'
      },
      moderate: {
        label: 'Moderate Score',
        description: 'Balanced expression - can adapt between both extremes'
      },
      high: {
        label: 'High Score',
        description: 'Trait is prominent - strong preference for this behavioral style'
      }
    },
    analogy: {
      title: 'Real-World Analogy',
      description: 'Think of percentiles like height: 50% means average height, 80% means taller than 80% of people, 20% means taller than only 20% of people. Same principle applies to behavioral traits.'
    }
  },

  cognitive: {
    title: 'Cognitive Assessment Overview',
    subtitle: 'The cognitive test measures mental ability and problem-solving capability.',
    whatItMeasures: {
      title: 'What It Measures',
      description: 'The cognitive assessment evaluates three core mental abilities:',
      items: {
        verbal: 'Verbal reasoning - understanding and analyzing written information',
        numerical: 'Numerical reasoning - working with numbers and quantitative data',
        abstract: 'Abstract reasoning - recognizing patterns and solving novel problems'
      }
    },
    difference: {
      title: 'How It Differs from Behavioral',
      description: 'Behavioral traits measure HOW you prefer to work (your style). Cognitive ability measures HOW WELL you solve complex problems (your capacity).',
      highlight: 'Key difference: Behavioral traits show preferences and tendencies. Cognitive scores show capability and learning speed.'
    },
    usingBoth: {
      title: 'Using Both Together for Hiring',
      description: 'The most effective hiring decisions combine both assessments:',
      items: {
        behavioral: 'Behavioral Profile → Will they enjoy the role and fit the culture?',
        cognitive: 'Cognitive Score → Can they handle the complexity and learn quickly?',
        combined: 'Together → Complete picture of fit, capability, and potential'
      }
    },
    format: {
      title: 'Test Format',
      description: '50 questions, 12 minutes, covering verbal, numerical, and abstract reasoning. Results show overall percentile and breakdown by reasoning type.'
    }
  },

  traits: {
    dominance: {
      letter: 'D',
      name: 'Drive',
      definition: 'Measures how direct and assertive someone is in achieving goals and driving results.',
      high: {
        title: 'High Score (60%+)',
        characteristics: 'Direct, decisive, results-oriented, takes charge, comfortable with conflict',
        strengths: 'Gets things done quickly, makes tough decisions, drives change',
        watchouts: 'May seem aggressive, can overlook feelings, might rush decisions'
      },
      low: {
        title: 'Low Score (40%-)',
        characteristics: 'Collaborative, supportive, consensus-seeking, conflict-avoidant, patient',
        strengths: 'Builds strong relationships, listens well, creates harmony',
        watchouts: 'May avoid necessary confrontation, can be indecisive, might defer too much'
      },
      moderate: {
        title: 'Moderate Score (40-60%)',
        characteristics: 'Balanced approach - assertive when needed, collaborative when appropriate'
      }
    },
    extraversion: {
      letter: 'C',
      name: 'Connection',
      definition: 'Measures how outgoing and social someone is, and how they gain energy.',
      high: {
        title: 'High Score (60%+)',
        characteristics: 'Outgoing, enthusiastic, social, energized by people, expressive',
        strengths: 'Builds networks easily, energizes teams, persuasive communicator',
        watchouts: 'May dominate conversations, can seem superficial, might overlook introverts'
      },
      low: {
        title: 'Low Score (40%-)',
        characteristics: 'Reserved, thoughtful, focused, prefers deep connections, observant',
        strengths: 'Thinks before speaking, listens deeply, works well independently',
        watchouts: 'May seem distant, can be overlooked, might miss networking opportunities'
      },
      moderate: {
        title: 'Moderate Score (40-60%)',
        characteristics: 'Ambivert - comfortable both socially and independently'
      }
    },
    patience: {
      letter: 'T',
      name: 'Task',
      definition: 'Measures preference for stability and consistency versus change and variety.',
      high: {
        title: 'High Score (60%+)',
        characteristics: 'Steady, consistent, reliable, prefers stability, patient, methodical',
        strengths: 'Maintains quality standards, builds lasting systems, calming presence',
        watchouts: 'May resist necessary change, can be slow to adapt, might avoid innovation'
      },
      low: {
        title: 'Low Score (40%-)',
        characteristics: 'Dynamic, fast-paced, thrives on change, variety-seeking, restless',
        strengths: 'Adapts quickly, drives innovation, multitasks effectively',
        watchouts: 'May seem impatient, can disrupt stability, might miss important details'
      },
      moderate: {
        title: 'Moderate Score (40-60%)',
        characteristics: 'Balanced - appreciates both stability and appropriate change'
      }
    },
    formality: {
      letter: 'O',
      name: 'Order',
      definition: 'Measures preference for structure and detail versus flexibility and big-picture thinking.',
      high: {
        title: 'High Score (60%+)',
        characteristics: 'Structured, detail-oriented, follows procedures, analytical, cautious',
        strengths: 'Ensures accuracy, maintains compliance, catches errors',
        watchouts: 'May seem rigid, can get lost in details, might slow progress'
      },
      low: {
        title: 'Low Score (40%-)',
        characteristics: 'Flexible, innovative, big-picture focused, spontaneous, risk-taking',
        strengths: 'Thinks creatively, adapts easily, spots opportunities',
        watchouts: 'May overlook details, can seem careless, might bypass important procedures'
      },
      moderate: {
        title: 'Moderate Score (40-60%)',
        characteristics: 'Balanced - structured when needed, flexible when appropriate'
      }
    },
    objectivity: {
      letter: 'S',
      name: 'Style',
      definition: 'Measures thinking style - analytical and data-driven versus intuitive and people-focused.',
      high: {
        title: 'High Score (60%+)',
        characteristics: 'Analytical, data-driven, logical, objective, facts-focused',
        strengths: 'Makes rational decisions, solves complex problems, brings objectivity',
        watchouts: 'May overlook emotions, can seem cold, might miss human factors'
      },
      low: {
        title: 'Low Score (40%-)',
        characteristics: 'Intuitive, people-focused, empathetic, relationship-oriented, values-driven',
        strengths: 'Reads people well, builds trust, considers impact on others',
        watchouts: 'May be swayed by emotions, can overlook data, might avoid tough decisions'
      },
      moderate: {
        title: 'Moderate Score (40-60%)',
        characteristics: 'Balanced - uses both logic and intuition appropriately'
      }
    }
  },

  width: {
    wide: {
      title: 'Wide Profile',
      definition: 'Large spread between highest and lowest scores (e.g., 85%-25%)',
      characteristics: [
        'More specialized behavioral tendencies',
        'Clear, pronounced strengths in certain areas',
        'May need support or development in other areas',
        'Strong predictors of role fit',
        'More defined personality signature'
      ],
      example: 'D=85%, C=75%, T=25%, O=30%, S=60%',
      useCase: 'Wide profiles are easier to match to specific roles - clear indicators of where they excel and where they may struggle'
    },
    narrow: {
      title: 'Narrow Profile',
      definition: 'Scores clustered around the middle (e.g., all 45%-55%)',
      characteristics: [
        'More adaptable and versatile',
        'Can flex across different situations',
        'May lack standout strengths',
        'Less predictable behavioral preferences',
        'Can fit multiple roles but may not excel in specialized positions'
      ],
      example: 'D=55%, C=48%, T=52%, O=45%, S=50%',
      useCase: 'Narrow profiles are generalists - adaptable but may need clear guidance on expectations and development focus'
    },
    interpretation: 'Neither wide nor narrow is better - it depends on the role. Specialized roles need wide profiles with the right pattern; generalist roles may benefit from narrower profiles.'
  },

  roleMatching: {
    title: 'Role Matching Examples',
    subtitle: 'See how different DCTOS profiles match specific job roles',
    keyTraits: 'Key Behavioral Traits',
    whyItFits: 'Why This Profile Fits',
    roles: {
      salesManager: {
        title: 'Sales Manager',
        pattern: 'High D, High C, Low T',
        traits: 'Assertive, persuasive, socially confident, fast-paced, competitive',
        fit: 'Drives results through charisma and urgency. Can influence clients and lead teams with confidence. Thrives in competitive, target-driven environments.'
      },
      accountant: {
        title: 'Accountant',
        pattern: 'Low D, High O, High S',
        traits: 'Detail-oriented, analytical, methodical, accurate, systematic',
        fit: 'Ensures precision and compliance through careful analysis. Comfortable working independently with numbers. Prefers structured, predictable work.'
      },
      customerSupport: {
        title: 'Customer Support Specialist',
        pattern: 'Low D, High T, Low S',
        traits: 'Patient, empathetic, steady, people-focused, conflict-avoidant',
        fit: 'Provides calm, consistent support. Naturally patient with frustrated customers. Values relationships over data. Creates harmony and trust.'
      },
      projectManager: {
        title: 'Project Manager',
        pattern: 'High D, High O',
        traits: 'Decisive, organized, drives accountability, plans thoroughly',
        fit: 'Keeps projects on track through structure and assertiveness. Can make tough calls while maintaining detailed plans. Balances people and process.'
      },
      creativeDirector: {
        title: 'Creative Director',
        pattern: 'High C, Low T, Low O',
        traits: 'Innovative, flexible, intuitive, energetic, big-picture focused',
        fit: 'Generates fresh ideas and inspires teams. Comfortable with ambiguity and change. Thinks creatively rather than analytically. Thrives on variety.'
      },
      dataAnalyst: {
        title: 'Data Analyst',
        pattern: 'Low C, High O',
        traits: 'Analytical, detail-focused, independent, logical, thorough',
        fit: 'Excels at finding patterns in data. Prefers working alone with numbers. Makes decisions based on evidence. Values accuracy over speed.'
      }
    },
    note: {
      title: 'Important:',
      description: 'These are ideal patterns, not absolute requirements. Many successful people adapt their natural style to fit their role. Use these as guides, not rigid rules.'
    }
  },

  applications: {
    readingProfiles: {
      title: 'How to Read a Profile',
      steps: [
        'Look at the overall pattern - which traits are high/low?',
        'Check the width - specialized or versatile?',
        'Consider trait combinations - how do they interact?',
        'Match against role requirements'
      ]
    },
    combinations: {
      title: 'Common Trait Combinations',
      patterns: [
        {
          traits: 'High D + Low T',
          meaning: 'Urgent/driven - wants fast results and quick changes',
          workplace: 'Great for crisis management, turnarounds, rapid execution'
        },
        {
          traits: 'High C + High D',
          meaning: 'Charismatic leader - socially confident and assertive',
          workplace: 'Excels in sales, leadership, public-facing roles'
        },
        {
          traits: 'Low D + High T',
          meaning: 'Steady supporter - collaborative and consistent',
          workplace: 'Ideal for customer service, operations, team coordination'
        },
        {
          traits: 'High O + High S',
          meaning: 'Analytical perfectionist - detail-oriented and logical',
          workplace: 'Perfect for finance, quality control, compliance'
        },
        {
          traits: 'Low O + Low S',
          meaning: 'Creative innovator - flexible and intuitive',
          workplace: 'Thrives in creative fields, innovation, people development'
        }
      ]
    },
    personaGap: {
      title: 'Self vs Work Behavior',
      definition: 'The difference between how someone naturally is (Self) and how they adapt at work (Work)',
      interpretation: 'Large gaps suggest behavioral adaptation or masking - this can be energizing for some, exhausting for others',
      redFlags: 'Very large gaps (>30 percentile points) across multiple traits may indicate role misfit or burnout risk'
    }
  },

  reasoning: {
    title: 'Deductive vs Inductive Thinking',
    subtitle: 'Understanding how people approach problems and make decisions',
    characteristics: 'Characteristics',
    example: 'Workplace Example',
    bestFor: 'Best Suited For',
    deductive: {
      title: 'Deductive (D > C)',
      subtitle: 'D higher than C',
      definition: 'Starts with general theories, rules, or principles and applies them to specific situations. Moves from the big picture to details.',
      characteristics: [
        'Rule-based and framework-first',
        'Theoretical and hypothesis-driven',
        'Applies existing knowledge to new situations',
        'Prefers structure and established methods'
      ],
      example: 'If our policy states X, then in this situation we should do Y',
      bestFor: 'Policy implementation, compliance work, standardized processes, applying proven frameworks'
    },
    inductive: {
      title: 'Inductive (C > D)',
      subtitle: 'C higher than D',
      definition: 'Starts with specific observations, data, or cases and builds toward general conclusions. Moves from details to the big picture.',
      characteristics: [
        'Data-driven and evidence-first',
        'Empirical and pattern-seeking',
        'Builds theories from observations',
        'Prefers exploration and discovery'
      ],
      example: 'We\'ve observed X happening repeatedly across multiple cases, so we can conclude Y',
      bestFor: 'Research, troubleshooting, innovation, strategy development, finding new patterns'
    },
    insight: {
      title: 'Key Insight',
      description: 'Neither reasoning style is inherently better. Effective problem-solvers use both depending on context. When you have proven frameworks, deductive thinking ensures consistency. When facing novel problems or seeking innovation, inductive thinking reveals new patterns.',
      connectionTitle: 'Connection to Behavioral Profiles',
      connection: 'High Style (S) individuals often naturally lean toward deductive reasoning—applying logical frameworks and rules. Low Style (S) individuals tend toward inductive reasoning—reading patterns in human behavior and context. However, this is a tendency, not a limitation, and people can develop both approaches.'
    }
  },

  executionSpeed: {
    title: 'The D-T Gap: Execution Speed',
    subtitle: 'The relationship between Drive (D) and Task (T) determines preferred pace of work and decision-making',
    gapLabel: 'Gap',
    fast: {
      title: 'D > T (Fast Execution)',
      subtitle: 'Higher Urgency',
      definition: 'When Drive exceeds Task, the person prefers fast, decisive action. The bigger the gap, the more urgent the execution style.',
      characteristics: [
        'Makes quick decisions',
        'Impatient with delays or slow processes',
        'Action-oriented, wants results now',
        'Drives fast change and transformation',
        'May seem pushy or rushed to others'
      ],
      example: '"Let\'s decide now and fix problems as they come up"',
      bestFor: 'Crisis management, sales closing, turnarounds, startup environments',
      watchouts: 'May rush quality, overlook important details, create stress for slower-paced colleagues'
    },
    deliberate: {
      title: 'T > D (Deliberate Pace)',
      subtitle: 'Steady & Methodical',
      definition: 'When Task exceeds Drive, the person prefers a measured, thorough approach. The bigger the gap, the more methodical the style.',
      characteristics: [
        'Takes time to consider options',
        'Values thoroughness over speed',
        'Maintains steady, consistent output',
        'Prefers proven processes',
        'May seem slow to urgent colleagues'
      ],
      example: '"Let\'s think this through properly before we commit"',
      bestFor: 'Quality assurance, operations, customer support, compliance, research',
      watchouts: 'May miss urgent deadlines, slow to respond in crisis, could frustrate fast-paced teammates'
    },
    balanced: {
      title: 'D ≈ T (Balanced Pace)',
      description: 'When Drive and Task are similar, the person can adapt their pace to the situation - urgent when needed, patient when appropriate.'
    },
    insight: {
      title: 'Key Insight',
      description: 'Neither fast nor slow execution is inherently better. The right pace depends on the role and context. Match execution speed expectations to role requirements.',
      howToUse: 'How to apply this:',
      howToUseDescription: 'High D-T gap roles (sales, crisis management) need D > T profiles. Low urgency roles (support, QA) benefit from T > D profiles. Balanced roles can work with either.'
    }
  },

  talentDesign: {
    title: 'Talent Design: Building the Right Team',
    subtitle: 'How to assemble high-performing teams based on company phase',
    introduction: 'Every team needs three forces working in harmony: Gas to drive growth, Glue to bind people together, and Brake to ensure excellence. The right balance depends on your company phase.',
    forcesTitle: 'The Three Forces',
    forces: {
      gas: {
        title: '⛽ GAS',
        trait: 'D (Drive)',
        description: 'Fuels growth and drives forward momentum. Gas pushes boundaries, takes calculated risks, and accelerates progress.',
        characteristics: [
          'Drives results and pushes for action',
          'Takes initiative without waiting for permission',
          'Challenges status quo and breaks through barriers',
          'Comfortable with uncertainty and rapid change'
        ]
      },
      glue: {
        title: '🧲 GLUE',
        trait: 'C (Connection)',
        description: 'Binds people together and builds culture. Glue creates cohesion, facilitates communication, and maintains team spirit.',
        characteristics: [
          'Builds relationships across teams and departments',
          'Facilitates communication and resolves conflicts',
          'Creates sense of belonging and shared purpose',
          'Energizes others and maintains morale'
        ]
      },
      brake: {
        title: '🛑 BRAKE',
        trait: 'O (Order)',
        description: 'Ensures excellence and maintains quality. Brake prevents costly mistakes, enforces standards, and protects what works.',
        characteristics: [
          'Catches errors before they become problems',
          'Maintains systems, processes, and documentation',
          'Ensures compliance and quality standards',
          'Provides stability and predictability'
        ]
      }
    },
    phasesTitle: 'Phase-Based Team Composition',
    phases: {
      startup: {
        title: '🚀 Startup Phase',
        primaryNeed: 'Speed & Growth',
        focus: 'Heavy on GAS (High D)',
        whatToLookFor: 'Hire drivers who push boundaries, tolerate ambiguity, move fast, and aren\'t afraid to break things. Too much brake will slow you down before you find product-market fit.'
      },
      scaling: {
        title: '📈 Growth/Scaling Phase',
        primaryNeed: 'Culture & Alignment',
        focus: 'Add GLUE (High C)',
        whatToLookFor: 'Bring in connectors who build bridges between growing teams, maintain culture as you scale, and keep everyone aligned on mission. Without glue, teams fragment and silos form.'
      },
      mature: {
        title: '🏢 Mature/Optimization Phase',
        primaryNeed: 'Excellence & Efficiency',
        focus: 'Strengthen BRAKE (High O)',
        whatToLookFor: 'Recruit quality guardians who enforce standards, prevent costly mistakes in established systems, and optimize what already works. Brake protects your gains and ensures sustainable excellence.'
      }
    },
    warningsTitle: 'Balance Warnings',
    warnings: [
      {
        condition: 'All GAS, no BRAKE',
        consequence: 'Chaos, burnout, quality issues, costly mistakes that could have been prevented'
      },
      {
        condition: 'All GLUE, no GAS',
        consequence: 'Lots of meetings and talk, but no execution or forward movement'
      },
      {
        condition: 'All BRAKE, no GAS',
        consequence: 'Paralysis by analysis, missed opportunities, slow death by over-optimization'
      }
    ],
    practicalApplication: {
      title: '💡 Key Takeaway',
      content: 'There\'s no universal "best" team composition. A startup needs different fuel than a Fortune 500 company. Audit your current team: What force are you missing? What force do you have too much of for your current phase? Then hire accordingly.'
    }
  }
};
